// MegaBuild: drive a mega run end-to-end.
//
// Lifecycle:
//   - On mount, GET /api/mega-build?status=running to surface any
//     in-flight runs the user navigated away from. Click any to track.
//   - Submitting the form POSTs /api/mega-build, switches the view to
//     phase-tracker mode for the new run.
//   - Phase tracker subscribes to the SSE stream for top-level state
//     transitions, and additionally polls /:id/progress every 3s for
//     the granular detail of the active phase (build events tail or
//     gauntlet per-archetype W/L).
//
// Fault-tolerance caveat: if the server process dies mid-run, the row
// stays at status='running' forever and the user has been charged 160
// credits for nothing. Listed in `MEGA_FAULT_TOLERANCE_TODO` in the
// mega-runner; a future PR will add a boot-time honest-erroring sweep.

import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Hammer } from "lucide-react";
import {
  createMegaBuild,
  getMegaProgress,
  listMegaBuilds,
  streamMegaBuild,
  type MegaBuildEvent,
  type MegaBuildJob,
  type MegaProgress,
  type GauntletJob,
} from "../lib/api";
import { useRefreshBalance } from "../lib/useBalance";

const PHASE_ORDER: MegaBuildJob["phase"][] = [
  "build",
  "gauntlet_v1",
  "edit",
  "gauntlet_v2",
  "present",
  "done",
];

const PHASE_LABEL: Record<MegaBuildJob["phase"], string> = {
  build: "1. Initial build",
  gauntlet_v1: "2. Gauntlet v1 (vs top-3 meta)",
  edit: "3. Edit pass",
  gauntlet_v2: "4. Gauntlet v2 (revised, vs top-3)",
  present: "5. Present best version",
  done: "✓ Complete",
  error: "✗ Error",
};

function fmtAge(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return `${Math.round(ms / 1000)}s ago`;
  if (ms < 60 * 60_000) return `${Math.round(ms / 60_000)}m ago`;
  return `${(ms / 3_600_000).toFixed(1)}h ago`;
}

export default function MegaBuild() {
  const refreshBalance = useRefreshBalance();
  const [brief, setBrief] = useState("");
  const [megaId, setMegaId] = useState<string | null>(null);
  const [job, setJob] = useState<MegaBuildJob | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const disposerRef = useRef<(() => void) | null>(null);

  // Surface in-flight runs from prior visits / other tabs.
  const runningQuery = useQuery({
    queryKey: ["mega-running"],
    queryFn: () => listMegaBuilds("running"),
    refetchInterval: megaId ? false : 5000,
    enabled: !megaId,
  });

  // Granular per-phase progress. Only polls while a mega is selected
  // and is still running. Stops on terminal status.
  const progressQuery = useQuery<MegaProgress>({
    queryKey: ["mega-progress", megaId],
    queryFn: () => getMegaProgress(megaId!),
    enabled: Boolean(megaId) && (job?.status === "running"),
    refetchInterval: 3000,
  });

  useEffect(() => {
    return () => disposerRef.current?.();
  }, []);

  function trackExisting(id: string): void {
    disposerRef.current?.();
    setMegaId(id);
    setStartError(null);
    const dispose = streamMegaBuild(id, (msg) => {
      if (msg.kind === "update" || msg.kind === "done") {
        setJob(msg.data);
        if (msg.kind === "done") refreshBalance();
      } else if (msg.kind === "error") {
        setStartError(msg.data.error);
      }
    });
    disposerRef.current = dispose;
  }

  async function handleRun(): Promise<void> {
    if (!brief.trim() || submitting) return;
    setSubmitting(true);
    setStartError(null);
    try {
      const { mega_id } = await createMegaBuild(brief.trim());
      refreshBalance();
      trackExisting(mega_id);
    } catch (e) {
      setStartError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  function reset(): void {
    disposerRef.current?.();
    disposerRef.current = null;
    setMegaId(null);
    setJob(null);
    setBrief("");
    setStartError(null);
  }

  const phaseIndex = job ? PHASE_ORDER.indexOf(job.phase) : -1;

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <header className="space-y-2">
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <Hammer className="h-6 w-6" /> Mega deck build
        </h1>
        <p className="text-sm text-muted-foreground">
          Build a deck, stress-test it against the top-3 meta archetypes, edit
          based on feedback, test again, then present whichever version
          performed better. Costs 160 credits and ~30 minutes per run.
        </p>
      </header>

      {/* Resume panel — only when not currently tracking a run. */}
      {!megaId && runningQuery.data && runningQuery.data.count > 0 && (
        <section className="space-y-2 rounded-md border border-amber-400 bg-amber-50 p-4 dark:bg-amber-950">
          <h2 className="text-sm font-medium">
            {runningQuery.data.count} run{runningQuery.data.count === 1 ? "" : "s"}{" "}
            in progress
          </h2>
          <ul className="space-y-1">
            {runningQuery.data.jobs.map((j) => (
              <li key={j.id} className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={() => trackExisting(j.id)}
                  className="flex flex-col items-start gap-0.5 text-left hover:underline"
                >
                  <span className="font-medium">
                    {PHASE_LABEL[j.phase]}
                  </span>
                  <span className="line-clamp-1 max-w-md text-xs text-muted-foreground">
                    {j.brief}
                  </span>
                </button>
                <span className="text-xs text-muted-foreground">
                  {fmtAge(j.started_at)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {!megaId && (
        <section className="space-y-3">
          <label className="block">
            <span className="mb-1 block text-sm font-medium">Brief</span>
            <textarea
              className="min-h-[120px] w-full resize-y rounded-md border border-input bg-background p-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
              placeholder="e.g. boros aggro on a $50 budget, value-creature focused"
              disabled={submitting}
            />
          </label>
          <button
            type="button"
            onClick={handleRun}
            disabled={!brief.trim() || submitting}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            {submitting ? "Starting..." : "Run mega build (160 credits)"}
          </button>
          {startError && (
            <p className="text-sm text-destructive">{startError}</p>
          )}
        </section>
      )}

      {megaId && job && (
        <PhaseTracker
          job={job}
          phaseIndex={phaseIndex}
          progress={progressQuery.data ?? null}
          onReset={reset}
        />
      )}
    </div>
  );
}

interface PhaseTrackerProps {
  job: MegaBuildJob;
  phaseIndex: number;
  progress: MegaProgress | null;
  onReset: () => void;
}

function PhaseTracker({ job, phaseIndex, progress, onReset }: PhaseTrackerProps) {
  const isTerminal = job.status === "complete" || job.status === "error";

  return (
    <section className="space-y-4">
      <div className="flex items-start justify-between rounded-md border p-4">
        <div className="space-y-1">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            Mega job
          </p>
          <p className="font-mono text-sm">{job.id}</p>
          <p className="text-xs text-muted-foreground">
            Started {fmtAge(job.started_at)}
            {job.completed_at && ` · finished ${fmtAge(job.completed_at)}`}
          </p>
          <p className="mt-1 text-sm">
            Status:{" "}
            <span
              className={
                job.status === "complete"
                  ? "text-green-600"
                  : job.status === "error"
                    ? "text-destructive"
                    : "text-amber-600"
              }
            >
              {job.status}
            </span>
          </p>
          {job.error && (
            <p className="mt-2 text-sm text-destructive">Error: {job.error}</p>
          )}
        </div>
        <button
          type="button"
          onClick={onReset}
          className="rounded-md border px-3 py-1.5 text-xs hover:bg-muted"
        >
          {isTerminal ? "Start new" : "Hide"}
        </button>
      </div>

      <ol className="space-y-2">
        {PHASE_ORDER.map((phase, i) => {
          const reached = phaseIndex >= i;
          const active = phaseIndex === i && job.status === "running";
          return (
            <li
              key={phase}
              className={
                "rounded-md border p-3 text-sm " +
                (active
                  ? "border-amber-400 bg-amber-50 dark:bg-amber-950"
                  : reached
                    ? "border-green-400 bg-green-50 dark:bg-green-950"
                    : "border-border opacity-60")
              }
            >
              <div className="flex items-center justify-between">
                <span className="font-medium">{PHASE_LABEL[phase]}</span>
                {active && (
                  <span className="flex items-center gap-1.5 text-xs">
                    <span
                      className="inline-block h-2 w-2 animate-pulse rounded-full bg-amber-500"
                      aria-hidden
                    />
                    running
                  </span>
                )}
              </div>
              <PhaseDetail phase={phase} job={job} active={active} progress={progress} />
            </li>
          );
        })}
      </ol>

      {job.final_deck_id && (
        <div className="rounded-md border border-green-400 bg-green-50 p-4 dark:bg-green-950">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            Final deck
          </p>
          <p className="font-mono text-sm">{job.final_deck_id}</p>
          <p className="mt-2 text-sm">
            <Link
              to="/decks"
              className="font-medium underline-offset-2 hover:underline"
            >
              Open in Decks library →
            </Link>
          </p>
        </div>
      )}
    </section>
  );
}

interface PhaseDetailProps {
  phase: MegaBuildJob["phase"];
  job: MegaBuildJob;
  active: boolean;
  progress: MegaProgress | null;
}

function PhaseDetail({ phase, job, active, progress }: PhaseDetailProps) {
  // Static IDs — always show whatever's been assigned, regardless of
  // whether the phase is currently active. Click-through links remain
  // useful for completed phases.
  const idLine = (() => {
    if (phase === "build" && job.initial_build_id) {
      return <BuildIdLine label="build_id" id={job.initial_build_id} />;
    }
    if (phase === "edit" && job.edit_build_id) {
      return <BuildIdLine label="build_id" id={job.edit_build_id} />;
    }
    if (phase === "gauntlet_v1" && job.gauntlet_v1_id) {
      return <GauntletIdLine id={job.gauntlet_v1_id} />;
    }
    if (phase === "gauntlet_v2" && job.gauntlet_v2_id) {
      return <GauntletIdLine id={job.gauntlet_v2_id} />;
    }
    return null;
  })();

  // Live detail — only when this phase is the active one.
  let live: React.ReactElement | null = null;
  if (active && progress) {
    if ((phase === "build" || phase === "edit") && progress.active_build_events) {
      live = <BuildEventTrail events={progress.active_build_events} />;
    } else if (
      (phase === "gauntlet_v1" || phase === "gauntlet_v2") &&
      progress.active_gauntlet
    ) {
      live = <GauntletProgress gauntlet={progress.active_gauntlet} />;
    }
  }

  if (!idLine && !live) return null;
  return (
    <div className="mt-2 space-y-2">
      {idLine}
      {live}
    </div>
  );
}

function BuildIdLine({ label, id }: { label: string; id: string }) {
  return (
    <p className="text-xs text-muted-foreground">
      {label}: <span className="font-mono">{id}</span>
    </p>
  );
}

function GauntletIdLine({ id }: { id: string }) {
  return (
    <p className="text-xs text-muted-foreground">
      gauntlet_id:{" "}
      <Link
        to={`/mega-build/gauntlet/${id}`}
        className="font-mono underline-offset-2 hover:underline"
      >
        {id}
      </Link>
    </p>
  );
}

/** Render a tail of deck_build_events as a compact "agent is doing X"
 *  trail. We surface the most informative event kinds (tool calls,
 *  classifier decisions, assistant text deltas) and skip the noisy
 *  ones (per-token deltas, run_start). */
function BuildEventTrail({ events }: { events: MegaBuildEvent[] }) {
  const interesting = events.filter((e) =>
    ["classifier", "tool_call", "tool_result", "assistant_text", "final_answer", "deck_saved", "run_error"].includes(e.kind),
  );
  if (interesting.length === 0) {
    return (
      <p className="text-xs italic text-muted-foreground">
        agent is starting…
      </p>
    );
  }
  const last = interesting[interesting.length - 1]!;
  const summary = summarizeBuildEvent(last);
  return (
    <div className="rounded-md bg-background/50 p-2 text-xs">
      <p>
        <span className="font-medium">latest:</span>{" "}
        <span className="font-mono">{last.kind}</span>
        {summary && <span className="text-muted-foreground"> · {summary}</span>}
      </p>
      <p className="mt-1 text-muted-foreground">
        {interesting.length} event{interesting.length === 1 ? "" : "s"} in tail
      </p>
    </div>
  );
}

function summarizeBuildEvent(ev: MegaBuildEvent): string | null {
  const p = ev.payload as Record<string, unknown> | null;
  if (!p) return null;
  if (ev.kind === "tool_call") {
    const name = typeof p.name === "string" ? p.name : "?";
    return `calling ${name}`;
  }
  if (ev.kind === "tool_result") {
    const name = typeof p.name === "string" ? p.name : "?";
    return `result from ${name}`;
  }
  if (ev.kind === "classifier") {
    const cat = typeof p.category === "string" ? p.category : null;
    return cat ? `category: ${cat}` : null;
  }
  if (ev.kind === "final_answer") return "writing final deck";
  if (ev.kind === "run_error") {
    const msg = typeof p.error === "string" ? p.error.slice(0, 80) : null;
    return msg;
  }
  return null;
}

/** Show per-archetype W-L for the active gauntlet, with the in-flight
 *  matchup highlighted. Pulled from gauntlet_jobs.outcome which the
 *  worker updates after each child playtest finishes. */
function GauntletProgress({ gauntlet }: { gauntlet: GauntletJob }) {
  const per = gauntlet.outcome.per_archetype;
  const finishedCount = per.filter((m) => m.games_played > 0).length;
  const totalArch = gauntlet.archetype_deck_ids.length;
  return (
    <div className="space-y-1.5 rounded-md bg-background/50 p-2 text-xs">
      <p>
        <span className="font-medium">phase:</span>{" "}
        <span className="font-mono">{gauntlet.phase ?? "starting"}</span>{" "}
        <span className="text-muted-foreground">
          · {finishedCount}/{totalArch} matchups complete
        </span>
      </p>
      {per.map((m, i) => {
        const isActive =
          finishedCount === i && gauntlet.status === "running";
        const finished = m.games_played >= gauntlet.games_per_archetype;
        return (
          <div
            key={i}
            className={
              "flex items-center justify-between " +
              (isActive ? "font-medium" : finished ? "" : "opacity-60")
            }
          >
            <span className="line-clamp-1">{m.archetype_theme}</span>
            <span className="font-mono">
              {m.games_played > 0
                ? `${m.user_wins}-${m.archetype_wins}-${m.draws}`
                : "—"}
              {m.games_played > 0 &&
                m.games_played < gauntlet.games_per_archetype &&
                ` (${m.games_played}/${gauntlet.games_per_archetype})`}
            </span>
          </div>
        );
      })}
    </div>
  );
}
